import React, { useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { isDefined } from '../../../../utils/variables';
import { GeodataSetupContext } from '../../../state/geodata-setup-state';
import { InfoBox } from '../../info-box/info-box';
import { ChooseAddons } from './pages/choose-addons';
import { GeodataFinish } from './pages/geodata-finish';
import { GeodataSettingsPage } from './pages/geodata-settings-page';
import { GeodataSummary } from './pages/geodata-summary';
import { SelectProcessType } from './pages/select-process-type';
import { UploadBoundary } from './pages/upload-boundary';
import { UploadGcp } from './pages/upload-gcp';
import { UploadImages } from './pages/upload-images';

/**
 * Responsible for creating a new geodata.
 */
export const GeodataSetup = (props: { disableInteractiveSetup?: boolean; height?: string }) => {
  const { t } = useTranslation();
  const context = useContext(GeodataSetupContext);

  useEffect(() => {
    if (props.disableInteractiveSetup) {
      context?.setDisableInteractiveSetup(true);
    }
  }, [props.disableInteractiveSetup, context]);

  if (!isDefined(context)) {
    return <InfoBox color="red">{t('unexpectedError', { ns: 'common' })}</InfoBox>;
  }

  if (context?.page === 'processType') {
    return (
      <SelectProcessType
        onSuccess={(data) => {
          context.setSelectedPackage(data);
          context?.setPage('settings');
        }}
      />
    );
  }

  if (context?.page === 'settings') {
    return (
      <Component height={props.height}>
        <GeodataSettingsPage />
      </Component>
    );
  }

  if (context?.page === 'gcp') {
    return (
      <Component height={props.height}>
        <UploadGcp />
      </Component>
    );
  }

  if (context?.page === 'boundary') {
    return (
      <Component height={props.height}>
        <UploadBoundary />
      </Component>
    );
  }

  if (context?.page === 'addons') {
    return (
      <Component height={props.height}>
        <ChooseAddons />
      </Component>
    );
  }

  if (context?.page === 'images') {
    return (
      <Component height={props.height}>
        <UploadImages />
      </Component>
    );
  }

  if (context?.page === 'summary') {
    return (
      <Component height={props.height}>
        <GeodataSummary />
      </Component>
    );
  }

  return (
    <Component height={props.height}>
      <GeodataFinish />
    </Component>
  );
};

const Component = styled.div<{ height?: string }>`
  ${({ height }) => height && `height: ${height};`}
`;
